// Similar to ConversationsWrapper.tsx
// Handles:
// - Route parameters and URL state management
// - Redux state management for events
// - Responsive layout for mobile/desktop
// - Dialog management for individual event details 

import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store';
import {
  fetchTimelineEvents,
  fetchEvents,
  setSelectedEvent,
  selectAllEvents,
  selectEventsLoading,
  selectHasMoreEvents,
  selectSelectedEventId,
} from 'src/features/timeline/events.slice';
import TimelineListView from './TimelineListView';
import EventDetail from './EventDetail';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import { Theme, SxProps } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Drawer from '@mui/material/Drawer';
import { useLocation } from 'react-router-dom';

interface StyleProps {
  box: SxProps<Theme>;
  dialog: SxProps<Theme>;
}

const getStyles = (darkMode: boolean): StyleProps => ({
  box: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    height: '100vh',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    bgcolor: darkMode ? 'grey.900' : 'background.default',
    color: darkMode ? 'grey.300' : 'text.primary',
    transition: 'all 0.3s ease',
    position: 'relative',
  },
  dialog: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: { xs: '100vw', sm: '100vw', md: '750px', lg: '850px', xl: '950px' },
    '& .MuiDialog-paper': {
      width: { xs: '100vw', sm: '100vw', md: '750px', lg: '850px', xl: '950px' },
      bgcolor: darkMode ? 'grey.900' : 'background.paper',
      color: darkMode ? 'grey.300' : 'text.primary',
      transition: 'all 0.3s ease',
      '& ::-webkit-scrollbar': {
        width: '8px',
        height: '8px'
      },
      '& ::-webkit-scrollbar-track': {
        bgcolor: darkMode ? 'grey.800' : 'grey.100'
      },
      '& ::-webkit-scrollbar-thumb': {
        bgcolor: darkMode ? 'grey.700' : 'grey.300',
        borderRadius: '4px',
        '&:hover': {
          bgcolor: darkMode ? 'grey.600' : 'grey.400'
        }
      }
    },
    '& .MuiBackdrop-root': {
      bgcolor: darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
    }
  },
});

const TimelineWrapper = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const events = useAppSelector(selectAllEvents);
  const loading = useAppSelector(selectEventsLoading);
  const selectedEventId = useAppSelector(selectSelectedEventId);
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { darkMode } = useAppSelector(state => state.theme);
  const styles = getStyles(darkMode);
  const [isLoadingFromUrl, setIsLoadingFromUrl] = useState(false);
  const [fetchedUrlEvent, setFetchedUrlEvent] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const urlEventId = new URLSearchParams(window.location.search).get('event_id');

  // Initial load of timeline events - ensure we only fetch once
  useEffect(() => {
    if (!initialLoadComplete && !loading) {
      setInitialLoadComplete(true);
      dispatch(fetchEvents({ 
        limit: 50,
        full_payload: false 
      }));
    }
  }, [dispatch, initialLoadComplete, loading]);

  // Handle URL event parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const eventId = urlParams.get('event_id');

    if (eventId && eventId !== 'null' && eventId !== 'undefined' && !fetchedUrlEvent) {
      setIsLoadingFromUrl(true);
      setFetchedUrlEvent(true);
      dispatch(setSelectedEvent(eventId));
      setIsLoadingFromUrl(false);
    } else if (!eventId) {
      setFetchedUrlEvent(false);
      setIsLoadingFromUrl(false);
    }
  }, [dispatch, urlEventId, fetchedUrlEvent]);

  // Reset tracking when path changes (without event_id)
  useEffect(() => {
    return () => {
      setFetchedUrlEvent(false);
    };
  }, [location.pathname]);

  const handleCloseDialog = () => {
    dispatch(setSelectedEvent(null));
    window.history.pushState({}, '', window.location.pathname);
    // On mobile, ensure any touch events are properly reset
    if (isSmallScreen) {
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }
  };

  /* --------------------- Responsive event viewer -------------------- */
  const EventDisplay = () => {
    // Guard against invalid ids
    if (!selectedEventId || selectedEventId === 'null' || selectedEventId === 'undefined') {
      return null;
    }

    // Common close props
    const commonProps = {
      open: true,
      onClose: handleCloseDialog,
      disableEscapeKeyDown: false as const,
      // @ts-ignore – MUI types accept these via union but TS doesn't infer with spread
      disableScrollLock: true,
    };

    const eventDetailNode = (
      <EventDetail eventId={selectedEventId} />
    );

    // Only render on small screens. On desktop, EventDetail is handled inside TimelineListView's split-view.
    if (isSmallScreen) {
      return (
        <Dialog
          {...commonProps}
          fullScreen
          closeAfterTransition
          sx={styles.dialog}
          PaperProps={{
            elevation: darkMode ? 0 : 24,
            sx: {
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }
          }}
          TransitionProps={{
            onExited: () => {
              dispatch(setSelectedEvent(null));
              window.history.pushState({}, '', window.location.pathname);
            }
          }}
          slotProps={{
            backdrop: {
              onClick: handleCloseDialog,
              sx: {
                '@media (max-width: 600px)': {
                  touchAction: 'none'
                }
              }
            }
          }}
        >
          {eventDetailNode}
        </Dialog>
      );
    }

    // Desktop – no dialog, handled in split view
    return null;
  };

  const handleEventSelect = (eventId: string) => {
    dispatch(setSelectedEvent(eventId));
    const url = new URL(window.location.href);
    url.searchParams.set('event_id', eventId);
    window.history.pushState({}, '', url.toString());
  };

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100vh', 
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'row',
      bgcolor: darkMode ? 'grey.900' : 'background.default',
      color: darkMode ? 'grey.300' : 'text.primary',
    }}>
      <TimelineListView 
        selectedEventId={selectedEventId}
        onEventSelect={handleEventSelect}
      />
      <EventDisplay />
    </Box>
  );
};

export default TimelineWrapper; 
